/**
 * SA ポータル: 契約更新バナー。
 * 契約終了が近い (または期限超過) の active SA に、視認性の高い更新申請ボタンを表示する。
 * 申請すると管理者へ通知され、管理者側 (SA管理) で更新が確定される。
 */
import { daysUntilDateJst } from '@goenhub/shared';
import { RotateCw } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import { useMySaOnboarding, useRequestSaRenewal } from '@/hooks/useMySaContract';

/** 終了まで何日以内なら更新バナーを出すか。 */
const RENEWAL_WINDOW_DAYS = 60;

export function SaRenewalBanner() {
  const { user } = useAuth();
  const { data: ob } = useMySaOnboarding(user?.uid);
  const requestMutation = useRequestSaRenewal();

  if (!ob || ob.status !== 'active' || !ob.contractEndDate) return null;

  const daysLeft = daysUntilDateJst(ob.contractEndDate, new Date());
  const requested = Boolean(ob.renewalRequestedAt);
  // 期限が遠く、未申請なら表示しない
  if (!requested && daysLeft > RENEWAL_WINDOW_DAYS) return null;

  const handleRequest = async () => {
    try {
      await requestMutation.mutateAsync();
      toast.success('更新申請を送信しました。管理者の確認をお待ちください。');
    } catch (err) {
      toast.error(
        `申請に失敗しました: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  return (
    <div className="rounded-md border-2 border-[var(--accent)] bg-[var(--accent-subtle)]/40 p-4 mb-4 flex items-center justify-between gap-4">
      <div>
        <div className="text-sm font-semibold">契約更新のご案内</div>
        <p className="text-xs text-[var(--text-muted)] mt-0.5">
          現在の契約は {ob.contractEndDate} まで
          {daysLeft >= 0 ? `（残り ${daysLeft} 日）` : '（期限超過）'}
          。継続をご希望の場合は更新を申請してください。
        </p>
      </div>
      {requested ? (
        <Badge variant="accent">更新申請済み</Badge>
      ) : (
        <Button
          type="button"
          variant="primary"
          onClick={handleRequest}
          disabled={requestMutation.isPending}
        >
          <RotateCw className="size-4 mr-1" />
          {requestMutation.isPending ? '送信中...' : '契約を更新する'}
        </Button>
      )}
    </div>
  );
}
